// 「水害に強い」のような目的から入れるショートカット。数値の妥当な閾値を知らないユーザーが
// 最初の 1 手を踏み出せるようにする。適用時は他の条件を既定値に戻してから patch を重ねる
// （切り替えで条件が積み重なり、該当なしになるのを防ぐ）。都道府県だけは絞り込みの「場所」なので維持する。
import { DEFAULT_FILTER_STATE, type FilterState } from "./filters";
import { DEFAULT_LANG, type Lang } from "./i18n";
import type { MetricKey } from "./metrics";

export interface Preset {
  id: string;
  label: string;
  description: string;
  patch: Partial<FilterState>;
  /** これらの指標が data/munis.json に 1 件も入っていなければ、この pill は出さない。 */
  requires?: MetricKey[];
}

interface PresetSpec {
  id: string;
  patch: Partial<FilterState>;
  requires?: MetricKey[];
  text: Record<Lang, { label: string; description: string }>;
}

const SPECS: PresetSpec[] = [
  {
    id: "flood-safe",
    patch: { floodMax: 0.05, tsunamiInlandOnly: true, tsunamiMax: null, sedimentMax: 0.05, metric: "flood" },
    text: {
      ja: { label: "水害に強い", description: "洪水 5% 以下・津波指定なし・土砂 5% 以下" },
      en: {
        label: "Flood resilient",
        description: "Flood ≤ 5%, no tsunami area designated, landslide ≤ 5%",
      },
    },
  },
  {
    id: "quake-low",
    patch: { quakeMax: 0.06, metric: "quake" },
    text: {
      ja: { label: "地震リスク低め", description: "30 年で震度 6 弱以上となる確率 6% 以下" },
      en: {
        label: "Lower quake risk",
        description: "6% or less chance of shindo 6-lower or above within 30 years",
      },
    },
  },
  {
    id: "warm-winter",
    patch: { tempJanMin: 5, metric: "tempJan" },
    text: {
      ja: { label: "冬あたたかい", description: "1 月の平均気温 5℃ 以上" },
      en: { label: "Mild winter", description: "Average January temperature of 5℃ or above" },
    },
  },
  {
    id: "cool-summer",
    patch: { tempAugMax: 24, metric: "tempAug" },
    text: {
      ja: { label: "夏すずしい", description: "8 月の平均気温 24℃ 以下" },
      en: { label: "Cool summer", description: "Average August temperature of 24℃ or below" },
    },
  },
  {
    id: "cheap-land",
    patch: { landPriceMax: 20000, metric: "landPrice" },
    text: {
      ja: { label: "地価が安い", description: "住宅地の地価中央値 2 万円/m2 以下" },
      en: { label: "Cheap land", description: "Median residential land price of 20,000 JPY/m² or below" },
    },
  },
  {
    id: "tokyo-100",
    patch: { hub: "tokyo", hubDistMax: 100, metric: "distTokyo" },
    text: {
      ja: { label: "東京 100km 圏", description: "東京駅から 100 km 以内" },
      en: { label: "Within 100 km of Tokyo", description: "100 km or less from Tokyo Station" },
    },
  },
  {
    id: "osaka-100",
    patch: { hub: "osaka", hubDistMax: 100, metric: "distOsaka" },
    text: {
      ja: { label: "大阪 100km 圏", description: "大阪駅から 100 km 以内" },
      en: { label: "Within 100 km of Osaka", description: "100 km or less from Osaka Station" },
    },
  },
  {
    id: "nagoya-100",
    patch: { hub: "nagoya", hubDistMax: 100, metric: "distNagoya" },
    text: {
      ja: { label: "名古屋 100km 圏", description: "名古屋駅から 100 km 以内" },
      en: { label: "Within 100 km of Nagoya", description: "100 km or less from Nagoya Station" },
    },
  },
  {
    id: "town-services",
    patch: { populationMin: 50000, metric: "foodStores" },
    requires: ["population", "foodStores"],
    text: {
      ja: { label: "生活の便がいい", description: "人口 5 万人以上・食料品店の多い順" },
      en: { label: "Everyday convenience", description: "Population 50,000 or more, most food stores first" },
    },
  },
];

const RESOLVED = new Map<Lang, Preset[]>();

export function getPresets(lang: Lang = DEFAULT_LANG): Preset[] {
  const cached = RESOLVED.get(lang);
  if (cached) return cached;
  const presets = SPECS.map((s) => ({
    id: s.id,
    patch: s.patch,
    ...(s.requires ? { requires: s.requires } : {}),
    ...s.text[lang],
  }));
  RESOLVED.set(lang, presets);
  return presets;
}

function patchKeys(preset: Pick<Preset, "patch">): (keyof FilterState)[] {
  return Object.keys(preset.patch) as (keyof FilterState)[];
}

export function isPresetActive(state: FilterState, preset: Pick<Preset, "patch">): boolean {
  return patchKeys(preset).every((key) => state[key] === preset.patch[key]);
}

/** 条件を既定値に戻した上で、非 active なら patch を重ねる（同じ pill でトグル）。 */
export function applyPreset(state: FilterState, preset: Pick<Preset, "patch">): FilterState {
  const base: FilterState = { ...DEFAULT_FILTER_STATE, pref: state.pref };
  return isPresetActive(state, preset) ? base : { ...base, ...preset.patch };
}
